import {FC, useCallback, useState} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {useBulkCompleteCourseEnrollments} from "@/app2/shared/data";
import {palette} from "@/constants";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {useBulkActionSnackbar} from "@/app2/shared/contexts/BulkActionSnackbarContext";

interface IProps {
	open: boolean;
	onClose: () => void;
	selectedUserIds: number[];
	courseId: number;
}

const buttonSx = {
	border: "none",
	boxShadow: "none",
	color: "primary.main",
	"&:hover": {
		backgroundColor: palette.orange08
	}
};

export const BulkCourseCompletionConfirmationModal: FC<IProps> = ({open, onClose, selectedUserIds, courseId}) => {
	const {t} = useSSTranslation();
	const {processInProgressSnackbar, errorSnackbar} = useCustomSnackbar();
	const {setSnackbarId} = useBulkActionSnackbar();
	const bulkCompleteMutation = useBulkCompleteCourseEnrollments(courseId);
	const [isLoading, setIsLoading] = useState(false);

	const handleConfirm = useCallback(() => {
		if (!open || isLoading) {
			return;
		}

		setIsLoading(true);

		bulkCompleteMutation.mutate(
			{userIds: selectedUserIds},
			{
				onSuccess: (data) => {
					const message = data.inProgress && data.userCount != null
						? t("bulk_course_completion_started", "Completing course for {{count}} user(s)...", {count: data.userCount})
						: t("bulk_course_completion_processing", "Processing course completion...");
					const id = processInProgressSnackbar(message, true);
					setSnackbarId("courseBulkComplete", id);
					setIsLoading(false);
					onClose();
				},
				onError: (error) => {
					errorSnackbar(t("bulk_course_completion_error", `Failed to complete users: ${error.message}`));
					setIsLoading(false);
				}
			}
		);
	}, [open, isLoading, selectedUserIds, bulkCompleteMutation, processInProgressSnackbar, setSnackbarId, errorSnackbar, t, onClose]);

	return (
		<Dialog
			open={open}
			onClose={(_event, reason) => {
				if (reason !== "backdropClick" && !isLoading) {
					onClose();
				}
			}}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			PaperProps={{
				sx: {
					bgcolor: "background.paperElevation3",
					borderColor: "background.paperElevation3"
				}
			}}
		>
			<DialogTitle id="alert-dialog-title">
				{t("bulk_course_completion_modal_title", "Confirm Bulk Completion")}
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="alert-dialog-description" variant="body1" color="text.primary">
					{t("bulk_course_completion_modal_body_copy", "This will mark all lessons in this course as complete for the selected users.", {count: selectedUserIds.length})}
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={onClose} sx={buttonSx} disabled={isLoading}>
					{t("team_progress_calendar_cancel", "Cancel")}
				</Button>
				<Button
					variant="text"
					onClick={handleConfirm}
					sx={buttonSx}
					disabled={isLoading}
					startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
				>
					{isLoading ? t("common_completing", "Completing...") : t("common_confirm", "Confirm")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
